import { TopicChannelCreateParams, UpdateChannelResponse } from '@@types/Api/Channel';
import { SlashCommand } from '@@types/Api/Command';
import Channel, { SharedChannel } from '@@types/Channel';
import ChannelMailSetting from '@@types/ChannelMailSetting';
import { CHANNEL_ID } from '@@types/common';
import Dialog from '@@types/Dialog';
import FolderFavorite from '@@types/FolderFavorite';
import Form from '@@types/Form';

import MeteringOverAlertContent from '~/components/MeteringUsage/OverContent';
import {
  ALERT_METERING_TYPE,
  IncomingHookTabType,
  MANAGE_HIDDEN_CHANNEL_TAB,
  MAX_FREE_MEMBER_COUNT,
  MODAL_NAMES,
  PANEL_TYPE,
} from '~/constants';
import ChannelArchiveForm from '~/forms/ChannelArchiveForm';
import ChannelForm from '~/forms/ChannelForm';
import ChannelInviteForm from '~/forms/ChannelInviteForm';
import ChannelMailForm from '~/forms/ChannelMailForm';
import CommandActionForm from '~/forms/CommandActionForm';
import DirectChannelForm from '~/forms/DirectChannelForm';
import DirectGroupChannelForm from '~/forms/DirectGroupChannelForm';
import FavoriteFolderForm from '~/forms/FavoriteFolderForm';
import IntegrationAppForm from '~/forms/IntegrationAppForm';
import ShareChannelForm from '~/forms/ShareChannelForm';
import SlashCommandForm from '~/forms/SlashCommandForm';
import SmsForm from '~/forms/SmsForm';
import TenantInviteForm from '~/forms/TenantInviteForm';
import i18n from '~/i18n';
import { getDefaultChannelColor, isPrivateChannel } from '~/services/channelService';
import { channelMailSettingStore } from '~/stores/ChannelMailSettingStore';
import { channelsStore } from '~/stores/ChannelsStore';
import { mainDetailUiState, threadDetailUiState } from '~/stores/DetailUiState';
import { favoritesStore } from '~/stores/FavoritesStore';
import { forwardLogStore } from '~/stores/ForwardLogStore';
import { globalSettingStore } from '~/stores/GlobalSettingStore';
import { interTenantMembersStore } from '~/stores/InterTenantMembersStore';
import { mailDomainStore } from '~/stores/MailDomainStore';
import { api } from '~/stores/providers/Api2';
import { replyLogStore } from '~/stores/ReplyLogStore';
import { sideAppsStore } from '~/stores/SideAppsStore';
import { slashCommandAppsStore } from '~/stores/SlashCommandAppsStore';
import { uiState } from '~/stores/UiState';
import { userStore } from '~/stores/UserStore';
import { extractInitialValues, makeFields } from '~/utils/commandUtils';
import { diffObjs, isNonEmpty, isTypeOf, preconcat } from '~/utils/fnUtil/fnUtil';
import { lift, mMaybe, runFutureValue } from '~/utils/monadUtil';
import Future, { encaseP, FutureInstance, resolve } from 'fluture';
import { isEmpty, trim } from 'lodash';
import moment from 'moment-timezone';
import {
  assoc,
  chain,
  curry,
  equals,
  evolve,
  head,
  identity,
  mergeDeepRight,
  mergeRight,
  omit,
  pick,
  pipe,
  propOr,
  propSatisfies,
  reject,
  split,
  union,
  when,
  without,
} from 'ramda';
import * as React from 'react';

import Actions from '../Actions';

type ChannelInviteParams = {
  channel: Channel;
  members: string[];
  created?: boolean;
};

type ChannelInviteCommandParams = {
  members: string[];
};

type ChannelMailSettingParams = {
  channelId: string;
};

type ChannelProfileSettingParams = {
  channelId: string;
};

type CommandActionParams = {
  channelId: string;
  dialog: Dialog;
  token: string;
  triggerId: string;
};

type ConfirmParams = {
  hideAction?: boolean;
  action?: any;
  cancel?: any;
  description: any;
};

type AlertParams = {
  description: React.ReactElement | string;
  onClick?: () => void;
  hideClose?: boolean;
};

type DirectChannelCreateParams = {
  members?: string[];
};

type FavoriteFolderEditParams = {
  folder: FolderFavorite;
};

type IncomingHookDeleteParams = {
  channelId: string;
  hookId: string;
};

type IncomingHookParams = {
  channelId: string;
};

type IntegrationAppEditParams = {
  integrationAppId: string;
};

type SlashCommandCreateParams = {
  integrationAppId: string;
};

type SlashCommandEditParams = {
  command: SlashCommand;
};

type SmsParams = {
  opponentId: string;
};

type ChannelArchiveParams = {
  channelId: string;
};

class ModalActions {
  constructor(private actions: Actions) {}

  private openModal = (params: { name: string; args?: any }) => {
    uiState.openModal(params);
  };

  private closeModal = (modalName: string) => {
    uiState.closeModal(modalName);
  };

  private handleSubmitChannelInvite = curry(async (params: ChannelInviteParams, form: Form) => {
    const { kicked, invitee, openChat } = form.values();
    const channel = channelsStore.getById(params.channel.channelId);
    const addedInvitee = without(channel.members, invitee);
    if (isNonEmpty(kicked)) {
      await this.actions.proxyActions.removeChannelMembers(channel.channelId, kicked);
    }

    if (isNonEmpty(addedInvitee)) {
      openChat
        ? await this.actions.channelMembersActions.inviteToChannel(channel.channelId, addedInvitee)
        : await this.actions.channelActions.copyAndArchiveChannel(channel.channelId, addedInvitee);
    }
    this.closeModal(MODAL_NAMES.CHANNEL_INVITE);
  });

  private uploadChannelImage = curry((channelId: string, form: Form): FutureInstance<any, CHANNEL_ID | void> => {
    const { files } = form.$('imageId');
    if (isEmpty(files)) {
      return resolve(channelId);
    }

    return this.actions.channelActions.uploadImage(channelId, files[0]);
  });

  private updateTitleDescription = curry(
    (channelId: CHANNEL_ID, form: Form): FutureInstance<never, UpdateChannelResponse> => {
      return pipe(
        pick(['title', 'description', 'colorCode']),
        evolve({ title: trim }),
        when(propSatisfies(isEmpty, 'colorCode'), omit(['colorCode'])),
        (formValues) => diffObjs(formValues, form.initials()),
        lift(identity),
        mMaybe(Future.of(channelId), encaseP(this.actions.proxyActions.updateChannel(channelId)))
      )(form.values()) as unknown as FutureInstance<never, UpdateChannelResponse>;
    }
  );

  private handleSubmitChannelMailSetting = curry((params: ChannelMailSettingParams, form: Form) => {
    const values = form.values();
    const fixedFormValues = evolve(
      {
        emailAddress: pipe(split('@'), head, preconcat(`@${values.domainPart}`)),
      },
      values
    );
    this.actions.channelActions.submitMailChannelSettingModal(mergeRight(params, fixedFormValues));
    this.closeModal(MODAL_NAMES.MAIL_CHANNEL_SETTING);
  });

  private handleSubmitChannelProfileSetting = curry((params: ChannelProfileSettingParams, form: Form) => {
    const { channelId } = params;
    pipe<any, any, any, any>(
      this.uploadChannelImage(channelId),
      chain(() => this.updateTitleDescription(channelId, form)),
      runFutureValue(identity)
    )(form);
    this.closeModal(MODAL_NAMES.UPDATE_CHANNEL);
  });

  private handleSubmitCommandAction = curry((params: CommandActionParams, form: Form) => {
    const { channelId, token, triggerId, dialog } = params;
    api.command.submitCommandModal({
      channelId,
      token,
      triggerId,
      callbackId: dialog.callbackId,
      form: form.values(),
    });
    this.closeModal(MODAL_NAMES.COMMAND_ACTION);
  });

  private handleSubmitDirectChannelCreate = curry((form: Form) => {
    const invitee = form.$('invitee').value;
    if (invitee.length === 1) {
      this.actions.channelActions.selectOrCreateDirectChannelByOpponentId(invitee[0]);
    } else {
      this.actions.channelActions.createGroupChannel({ memberIds: invitee });
    }
    this.closeModal(MODAL_NAMES.CREATE_DIRECT_CHANNEL);
  });

  private handleSubmitFavoriteFolderCreate = (form: Form) => {
    this.actions.favoriteActions.addFolder(form.$('title').value.trim());
    this.closeModal(MODAL_NAMES.ADD_FAVORITE_FOLDER);
  };

  private handleSubmitFavoriteFolderEdit = curry((params: FavoriteFolderEditParams, form: Form) => {
    this.actions.favoriteActions.updateTitle(form.$('title').value.trim(), params.folder.id);
    this.closeModal(MODAL_NAMES.CHANGE_FAVORITE_TITLE);
  });

  private handleSubmitIntegrationAppCreate = (form: Form) => {
    this.actions.slashCommandAppActions.createApp(form);
    this.closeModal(MODAL_NAMES.ADD_INTEGRATION_APP);
  };

  private handleSubmitIntegrationAppEdit = curry((params: IntegrationAppEditParams, form: Form) => {
    this.actions.slashCommandAppActions.updateApp(params.integrationAppId, form);
    this.closeModal(MODAL_NAMES.EDIT_INTEGRATION_APP);
  });

  private handleSubmitSlashCommandCreate = (form: Form) => {
    this.actions.slashCommandAppActions.createCommand(form);
    this.closeModal(MODAL_NAMES.CREATE_SLASH_COMMAND);
  };

  private handleSubmitSlashCommandEdit = curry((params: SlashCommandEditParams, form: Form) => {
    pipe<any, any, any, any>(
      pick(['name', 'requestUrl', 'description', 'parameterHint']),
      mergeDeepRight(params.command),
      this.actions.slashCommandAppActions.updateUserAppCommand
    )(form.values());
    this.closeModal(MODAL_NAMES.EDIT_SLASH_COMMAND);
  });

  private handleSubmitSms = curry((params: SmsParams, form: Form) => {
    const opponent = interTenantMembersStore.getById(params.opponentId);
    api.phone.sendSms(userStore.id, {
      calleeName: opponent.name,
      calleePhoneNumber: opponent.phone,
      message: form.$('message').value,
    });
    this.closeModal(MODAL_NAMES.SMS);
  });

  private handleSubmitTopicChannelCreate = curry(async (params: TopicChannelCreateParams, form: Form) => {
    const formValues = form.values();
    const createParams = evolve({ title: trim }, omit(['shared'], formValues));
    const channel: Channel = await this.actions.proxyActions.createTopicChannel(formValues.shared, createParams);
    this.closeModal(MODAL_NAMES.CREATE_TOPIC_CHANNEL);
    if (isNonEmpty(channel)) {
      if (formValues.shared) {
        channel.members = [userStore.id];
      }

      this.openChannelInvite({ channel, members: params?.members ?? [], created: true });
    }
  });

  private handleSubmitTenantMembersInvite = async (form: Form) => {
    const { invitee, role } = form.values();
    const memberCount = await api.member.fetchMemberCount();
    if (
      role === 'member' &&
      !globalSettingStore.subscription.agreePayment &&
      globalSettingStore.isBasicPlan &&
      memberCount + invitee.length > MAX_FREE_MEMBER_COUNT
    ) {
      this.openPayment();
    } else {
      this.actions.memberActions.inviteTenantMembers(form);
      this.closeModal(MODAL_NAMES.INVITE_TENANT_MEMBERS);
    }
  };

  private handleSubmitShareChannel = (form: Form) => {
    this.actions.memberActions.inviteSharedChannel(form);
    this.closeModal(MODAL_NAMES.SHARE_CHANNEL_MODAL);
  };

  private createChannelInviteForm = (params: ChannelInviteParams) => {
    const { channel, members } = params;
    const invitee = reject(equals(userStore.id), channel?.members ?? []).sort();
    const form = new ChannelInviteForm(
      { values: { invitee, kicked: [], openChat: true } },
      { hooks: { onSuccess: this.handleSubmitChannelInvite(params) } }
    );

    if (isNonEmpty(members)) {
      (form as Form).$('invitee').set(reject(equals(userStore.id), union(invitee, members)).sort());
    }

    return form;
  };

  private splitEmailAddress = ({ emailAddress, emailAddressCandidate }: ChannelMailSetting) => {
    const splitParts = split('@');
    if (isNonEmpty(emailAddress)) {
      return splitParts(emailAddress);
    }

    if (isNonEmpty(emailAddressCandidate)) {
      const [localPart] = splitParts(emailAddressCandidate ?? '');
      return [localPart, ''];
    }
  };

  private fetchCandidateEmailAddress = (params: ChannelMailSettingParams) => {
    const mailSetting = channelMailSettingStore.getMailSetting(params.channelId);
    if (isEmpty(mailSetting?.emailAddressCandidate)) {
      return channelMailSettingStore.fetchCandidate(params.channelId);
    }

    return Promise.resolve(mailSetting);
  };

  private createChannelMailSettingForm = (params: ChannelMailSettingParams) => {
    const mailSetting = channelMailSettingStore.getMailSetting(params.channelId);
    const { emailTitle, emailDescription } = mailSetting;
    const [localPart, domainPart = ''] = this.splitEmailAddress(mailSetting);
    return new ChannelMailForm(
      {
        values: {
          emailAddress: localPart,
          domainPart,
          emailTitle,
          emailDescription,
        },
      },
      { hooks: { onSuccess: this.handleSubmitChannelMailSetting(params) } }
    );
  };

  private createChannelProfileSettingForm = (params: ChannelProfileSettingParams) => {
    const channel = channelsStore.getById(params.channelId);
    const { colorCode, description, image, title } = channel;
    const imageId = propOr('', 'original', image);
    const values = {
      title,
      description,
      colorCode: isEmpty(imageId) ? colorCode || getDefaultChannelColor(channel) : '',
      objectUrl: '',
      imageId,
    };
    const Form = isEmpty(title) ? DirectGroupChannelForm : ChannelForm;
    return new Form(
      { values },
      { hooks: { onSuccess: this.handleSubmitChannelProfileSetting(params) } },
      this.actions.proxyActions
    );
  };

  private createCommandActionForm = (params: CommandActionParams) => {
    const fields = makeFields(params.dialog.elements);
    const values = extractInitialValues(params.dialog.elements);
    return new CommandActionForm({ fields, values }, { hooks: { onSuccess: this.handleSubmitCommandAction(params) } });
  };

  private createDirectChannelCreateForm = () => {
    return new DirectChannelForm(
      { values: { invitee: [], kicked: [] } },
      { hooks: { onSuccess: this.handleSubmitDirectChannelCreate() } }
    );
  };

  private createFavoriteFolderForm = (handleSubmit: (form: Form) => any, params?: FavoriteFolderEditParams) => {
    return new FavoriteFolderForm(
      { values: { title: params?.folder?.title ?? '' } },
      { hooks: { onSuccess: handleSubmit } },
      { favoritesStore }
    );
  };

  private createFavoriteFolderCreateForm = () => {
    return this.createFavoriteFolderForm(this.handleSubmitFavoriteFolderCreate);
  };

  private createFavoriteFolderEditForm = (params: FavoriteFolderEditParams) => {
    return this.createFavoriteFolderForm(this.handleSubmitFavoriteFolderEdit(params), params);
  };

  private createIntegrationAppCreateForm = () => {
    return new IntegrationAppForm(
      { values: { name: '', description: '', iconAttachId: '' } },
      { hooks: { onSuccess: this.handleSubmitIntegrationAppCreate } },
      { tenantId: userStore.tenantId, api: api }
    );
  };

  private createIntegrationAppEditForm = (params: IntegrationAppEditParams) => {
    const app = slashCommandAppsStore.getUserAppById(params.integrationAppId);
    if (!app) {
      return null;
    }
    const { name, description, iconAttachId } = app;

    return new IntegrationAppForm(
      { values: { name, description, iconAttachId, iconPreviewUrl: '' } },
      { hooks: { onSuccess: this.handleSubmitIntegrationAppEdit(params) } },
      { tenantId: userStore.tenantId, api: api }
    );
  };

  private createSlashCommandForm = curry(
    (handleSubmit: (form: Form) => void, params: SlashCommandCreateParams | SlashCommandEditParams) => {
      let values: { appId?: string } & Partial<SlashCommand>;
      if (isTypeOf('integrationAppId', params)) {
        values = { appId: (params as SlashCommandCreateParams).integrationAppId };
      } else if (isTypeOf('command', params)) {
        const editParams = params as SlashCommandEditParams;
        values = editParams.command;
        values.appId = editParams.command.integrationAppId;
      }
      return new SlashCommandForm(
        { values },
        { hooks: { onSuccess: handleSubmit } },
        { appId: values.appId, api: api, isGov: globalSettingStore.isGov }
      );
    }
  );

  private createSlashCommandCreateForm = (params: SlashCommandCreateParams) => {
    return this.createSlashCommandForm(this.handleSubmitSlashCommandCreate, params);
  };

  private createSlashCommandEditForm = (params: SlashCommandEditParams) => {
    return this.createSlashCommandForm(this.handleSubmitSlashCommandEdit(params), params);
  };

  private createSmsForm = (params: SmsParams) => {
    return new SmsForm({ values: { message: '' } }, { hooks: { onSuccess: this.handleSubmitSms(params) } });
  };

  private createTopicChannelCreateForm = (params: TopicChannelCreateParams) => {
    return new ChannelForm(
      {},
      { hooks: { onSuccess: this.handleSubmitTopicChannelCreate(params) } },
      this.actions.proxyActions
    );
  };

  private createTenantMembersInviteForm = () => {
    const form = new TenantInviteForm({}, { handlers: { onSubmit: this.handleSubmitTenantMembersInvite } });
    const guestValidityDays = globalSettingStore.guestGeneralSetting?.value?.guestValidityDays || 30;
    (form as Form).$('expirationDate').set(new Date(moment().add(guestValidityDays, 'days').format('YYYY-MM-DD')));
    return form;
  };

  private createShareChannelForm = (channel: SharedChannel) => {
    return new ShareChannelForm(
      {
        values: {
          channelId: channel.channelId,
        },
      },
      { handlers: { onSubmit: this.handleSubmitShareChannel } }
    );
  };

  openMeeting = async (command: 'meeting' | 'webex', channelId?: string, fromThread = false) => {
    await this.openConfirm({
      description: i18n.t(
        'openCamModal.question',
        '화상회의를 실행하시겠습니까?\n화상회의 실행 시, 대화방에 화상회의 초대 메시지를 전송합니다.'
      ),
    });

    if (!channelId && fromThread) {
      await this.actions.threadChannelActions.createThreadChannelFromEditorIfNeed();
      channelId = threadDetailUiState.contentsChannelId;
    }
    this.actions.slashCommandAppActions.executeCommand(channelId, { text: '', name: `/${command}` });
  };

  openChannelHide = async (channelId: string) => {
    await this.openConfirm({
      description: i18n.t(
        'hideChannelModal.description',
        '대화방을 숨겨도 대화 내용이 삭제되지는 않습니다.\n추후 숨긴 대화방은 대화방 검색으로 찾을 수 있습니다.\n대화방을 숨기시겠습니까?'
      ),
    });
    this.actions.channelActions.toggleDisplay(channelId);
  };

  openChannelImageRemove = () => {
    return this.openConfirm({
      description: i18n.t('removeChannelImage.description'),
    });
  };

  openChannelInvite = (params: ChannelInviteParams) => {
    const channelId = params.channel.channelId;
    const { id: myId } = userStore;
    const isAdminMode = this.actions.channelActions.checkAdminMode(channelId);
    if (isAdminMode && !channelsStore.isAdmin(channelId, myId)) {
      const description = i18n.t('invite.channel.adminAlert');
      this.openAlert({ description });
      return;
    }

    const form = this.createChannelInviteForm(params);
    this.openModal({
      name: MODAL_NAMES.CHANNEL_INVITE,
      args: assoc('form', form, params),
    });
  };

  private handleSubmitChannelInviteCommand = curry((channelId: string, form: Form) => {
    const { channelActions, channelMembersActions } = this.actions;
    const { invitee, openChat } = form.values();

    if (openChat) {
      channelMembersActions.inviteToChannel(channelId, invitee);
    } else {
      channelActions.copyAndArchiveChannel(channelId, invitee);
    }

    this.closeModal(MODAL_NAMES.CHANNEL_INVITE_COMMAND);
  });

  private createChannelInviteCommandForm = (params: ChannelInviteCommandParams) => {
    const { members } = params;
    const form = new ChannelInviteForm(
      { values: { invitee: [], kicked: [], openChat: true } },
      { hooks: { onSuccess: this.handleSubmitChannelInviteCommand(mainDetailUiState.contentsChannelId) } }
    );

    if (isNonEmpty(members)) {
      (form as Form).$('invitee').set(members.sort());
    }

    return form;
  };

  openChannelInviteCommand = (params: ChannelInviteCommandParams) => {
    const channelId = mainDetailUiState.contentsChannelId;
    const form = this.createChannelInviteCommandForm(params);
    this.openModal({
      name: MODAL_NAMES.CHANNEL_INVITE_COMMAND,
      args: assoc('form', form, { ...params, channelId }),
    });
  };

  private getChannelLeaveDescription = (isPrivateChannel) => {
    if (!isPrivateChannel) {
      return i18n.t('channelLeaveModal.direct');
    }
    return i18n.t('channelLeaveModal.private');
  };

  openChannelLeave = async (channelId: string) => {
    const isAdminMode = this.actions.channelActions.checkAdminMode(channelId);
    const myId = userStore.id;
    if (isAdminMode && channelsStore.isOnlyAdmin(channelId, myId)) {
      const description = i18n.t('channelLeaveModal.onlyAdmin');
      this.openAlert({ description });
      return;
    }
    const channel = channelsStore.getById(channelId);

    const description = this.getChannelLeaveDescription(isPrivateChannel(channel));
    await this.openConfirm({ description });
    this.actions.proxyActions.leaveChannel(channelId, myId);
    replyLogStore.removeReplyLog(channelId);
    forwardLogStore.removeForwardLog(channelId);
  };

  openChannelMailSetting = async (params: ChannelMailSettingParams) => {
    await this.fetchCandidateEmailAddress(params);
    if (mailDomainStore.domainList.length === 0) {
      await this.actions.mailDomainActions.fetchDomains();
    }
    const form = this.createChannelMailSettingForm(params) as Form;
    const domainPartField = form.$('domainPart');
    if (!domainPartField.value) {
      domainPartField.set(mailDomainStore.domainSelectOptionList?.[0].value);
    }

    this.openModal({
      name: MODAL_NAMES.MAIL_CHANNEL_SETTING,
      args: assoc('form', form, params),
    });
  };

  openChannelMailSettingUnregister = async (channelId: string) => {
    await this.openConfirm({
      description: i18n.t('mailChannel.unregister', '대화방 메일 설정을 해제하겠습니까?'),
    });
    this.actions.channelActions.unregisterChannelMail(channelId);
  };

  openChannelProfileSetting = (params: ChannelProfileSettingParams) => {
    const form = this.createChannelProfileSettingForm(params);
    this.openModal({
      name: MODAL_NAMES.UPDATE_CHANNEL,
      args: assoc('form', form, params),
    });
  };

  openAlert = (params: AlertParams): Promise<any> => {
    return new Promise((resolve, reject) => {
      this.openModal({
        name: MODAL_NAMES.ALERT,
        args: mergeRight({ resolve, reject }, params),
      });
    });
  };

  openConfirm = (params: ConfirmParams): Promise<any> => {
    return new Promise((resolve, reject) => {
      this.openModal({
        name: MODAL_NAMES.CONFIRM,
        args: mergeRight({ resolve, reject }, params),
      });
    });
  };

  openCommandAction = (params: CommandActionParams) => {
    const form = this.createCommandActionForm(params);
    this.openModal({
      name: MODAL_NAMES.COMMAND_ACTION,
      args: assoc('channelId', params.channelId, assoc('form', form, params)),
    });
  };

  openDirectChannelCreate = (params?: DirectChannelCreateParams) => {
    const form = this.createDirectChannelCreateForm();
    this.openModal({
      name: MODAL_NAMES.CREATE_DIRECT_CHANNEL,
      args: assoc('form', form, params),
    });
  };

  openDirectChannelCreateWithMembers = (params: DirectChannelCreateParams) => {
    const form = this.createDirectChannelCreateForm() as Form;
    form.$('invitee').set(reject(equals(userStore.id), params?.members ?? []));
    this.openModal({
      name: MODAL_NAMES.CREATE_DIRECT_CHANNEL,
      args: assoc('form', form, params),
    });
  };

  openFavoriteFolderCreate = () => {
    const form = this.createFavoriteFolderCreateForm();
    this.openModal({ name: MODAL_NAMES.ADD_FAVORITE_FOLDER, args: { form } });
  };

  openFavoriteFolderEdit = (params: FavoriteFolderEditParams) => {
    const form = this.createFavoriteFolderEditForm(params);
    this.openModal({
      name: MODAL_NAMES.CHANGE_FAVORITE_TITLE,
      args: assoc('form', form, params),
    });
  };

  openFavoriteFolderRemove = async (favoriteId: string) => {
    await this.openConfirm({
      description: i18n.t('removeFavorite.description'),
    });
    this.actions.favoriteActions.unsetFavoriteFolder(favoriteId);
  };

  openIncomingHook = (params: IncomingHookParams) => {
    this.openModal({
      name: MODAL_NAMES.INCOMING_HOOK_INTEGRATION,
      args: params,
    });
  };

  openIncomingHookDeleteConfirm = async (params: IncomingHookDeleteParams) => {
    await this.openConfirm({
      description: i18n.t(
        'incomingHookDelete.description',
        '삭제 후 다시 복구할 수 없습니다. 해당 항목을 삭제하시겠습니까?'
      ),
    });
    this.actions.hookActions.deleteHook(params);
  };

  openIncomingHookLimitAlert = () => {
    this.openModal({
      name: MODAL_NAMES.ALERT,
      args: {
        description: i18n.t('incomingHook.tenantLimit', {
          defaultValue:
            '{{tenantName}}에서 추가할 수 있는 연동 서비스 개수를 초과했습니다. 서비스 추가를 위해서는 관리자에게 문의해 주세요.',
          tenantName: userStore.tenant.name,
        }),
      },
    });
  };

  openIncomingHookResultAlert = () => {
    this.openModal({
      name: MODAL_NAMES.ALERT,
      args: {
        description: i18n.t(
          'incomingHook.result',
          '연동 URL이 생성되었습니다. 연동 중인 서비스 화면에서 연동 URL을 복사해서 사용할 수 있습니다.'
        ),
        onClick: () => uiState.setIncomingHookIntegrationTab(IncomingHookTabType.InServices),
      },
    });
  };

  openIntegrationAppCreate = () => {
    const form = this.createIntegrationAppCreateForm();
    this.openModal({ name: MODAL_NAMES.ADD_INTEGRATION_APP, args: { form } });
  };

  openIntegrationAppEdit = (params: IntegrationAppEditParams) => {
    const form = this.createIntegrationAppEditForm(params);
    this.openModal({
      name: MODAL_NAMES.EDIT_INTEGRATION_APP,
      args: assoc('form', form, params),
    });
  };

  openPayment = () => {
    this.openModal({ name: MODAL_NAMES.PAYMENT_ALERT });
  };

  openSideAppUnavailableAlert = (sideAppId: string) => {
    this.openModal({
      name: MODAL_NAMES.ALERT,
      args: {
        description: i18n.t('SERVER_SIDE_APP_UNAVAILABLE_ERROR'),
        onClick: () => {
          uiState.setPanelType(PANEL_TYPE.NONE);
          uiState.setPanelSubType('');
          sideAppsStore.removeSideApp(sideAppId);
        },
      },
    });
  };

  openSlashCommandCreate = (params: SlashCommandCreateParams) => {
    const form = this.createSlashCommandCreateForm(params);
    this.openModal({
      name: MODAL_NAMES.CREATE_SLASH_COMMAND,
      args: assoc('form', form, params),
    });
  };

  openSlashCommandEdit = (params: SlashCommandEditParams) => {
    const form = this.createSlashCommandEditForm(params);
    this.openModal({
      name: MODAL_NAMES.EDIT_SLASH_COMMAND,
      args: assoc('form', form, params),
    });
  };

  openSms = (params: SmsParams) => {
    const form = this.createSmsForm(params);
    this.openModal({
      name: MODAL_NAMES.SMS,
      args: assoc('form', form, params),
    });
  };

  openTenantMembersInvite = () => {
    const form = this.createTenantMembersInviteForm();
    this.openModal({ name: MODAL_NAMES.INVITE_TENANT_MEMBERS, args: { form } });
  };

  openTopicChannelCreate = (params?: TopicChannelCreateParams) => {
    const form = this.createTopicChannelCreateForm(params);
    this.openModal({
      name: MODAL_NAMES.CREATE_TOPIC_CHANNEL,
      args: assoc('form', form, params),
    });
  };

  private createChannelArchiveForm = (params: ChannelArchiveParams) => {
    return new ChannelArchiveForm({ values: { channelId: params.channelId, type: 'inService' } }, {});
  };

  openChannelArchive = (params: ChannelArchiveParams) => {
    const form = this.createChannelArchiveForm(params);
    this.openModal({
      name: MODAL_NAMES.ARCHVE_CHANNEL,
      args: assoc('form', form, params),
    });
  };

  openConversionNotice = (params: { expiredAt: number }) => {
    this.openModal({
      name: MODAL_NAMES.CONVERSION_NOTICE,
      args: { expiredAt: params.expiredAt },
    });
  };

  openManageHiddenChannel = () => {
    this.actions.channelActions.handleClickHiddenChannelTab({ id: MANAGE_HIDDEN_CHANNEL_TAB.Hidden });
    this.openModal({ name: MODAL_NAMES.MANAGE_HIDDEN_CHANNEL });
  };

  openMeteringAlert = (params: { isMeChannel: boolean; alertType: ALERT_METERING_TYPE }) => {
    this.openModal({
      name: MODAL_NAMES.METERING_USAGE_ALERT,
      args: params,
    });
  };

  openOverUsageAlert = (params: { channelId: string }) => {
    const isMeChannel = this.actions.channelActions.isMeChannelId(params.channelId);
    this.openAlert({
      description: <MeteringOverAlertContent isMeChannel={isMeChannel} />,
    });
  };

  openShareChannelModal = (channel: SharedChannel) => {
    const form = this.createShareChannelForm(channel);
    this.openModal({
      name: MODAL_NAMES.SHARE_CHANNEL_MODAL,
      args: { form, channel },
    });
  };

  openNotChannelHostTenantAlert = () => {
    this.openAlert({
      description: i18n.t('channel.shareSetting.notHostAlert', '채널 소유 테넌트에서만 공유할 수 있습니다.'),
    });
  };

  public openThreadChannel = () => {};
}

export default ModalActions;
